/**
 * The emoji dataset, loaded on first use.
 *
 * Bundled rather than fetched from a CDN — the app talks to its own origin
 * only — but in its own chunk, imported dynamically, so nobody pays for half a
 * megabyte of emoji names until they open a picker or type `:`.
 */

export type Emoji = {
  /** The character(s) themselves. */
  unicode: string;
  /** "grinning face". */
  label: string;
  /** Lowercased label, tags and shortcodes, for search. */
  keywords: string[];
  /** GitHub-style names without colons: "grinning", "+1". */
  shortcodes: string[];
  group: number;
  order: number;
};

export type EmojiCategory = { key: string; label: string; emoji: Emoji[] };

export type EmojiData = {
  categories: EmojiCategory[];
  all: Emoji[];
  byShortcode: Map<string, Emoji>;
};

type RawEmoji = { unicode: string; label: string; hexcode: string; group?: number; order?: number; tags?: string[] };

/** emojibase's groups, without "components" (bare skin tones, hair). */
const GROUPS: { group: number; key: string; label: string }[] = [
  { group: 0, key: "smileys", label: "Smileys & emotion" },
  { group: 1, key: "people", label: "People & body" },
  { group: 3, key: "nature", label: "Animals & nature" },
  { group: 4, key: "food", label: "Food & drink" },
  { group: 5, key: "travel", label: "Travel & places" },
  { group: 6, key: "activities", label: "Activities" },
  { group: 7, key: "objects", label: "Objects" },
  { group: 8, key: "symbols", label: "Symbols" },
  { group: 9, key: "flags", label: "Flags" },
];

let loading: Promise<EmojiData> | null = null;
let loaded: EmojiData | null = null;

export function emojiDataIfLoaded(): EmojiData | null {
  return loaded;
}

export function loadEmojiData(): Promise<EmojiData> {
  loading ??= Promise.all([
    import("emojibase-data/en/compact.json"),
    import("emojibase-data/en/shortcodes/github.json"),
  ]).then(([compact, github]) => {
    const raw = compact.default as unknown as RawEmoji[];
    const codes = github.default as unknown as Record<string, string | string[]>;
    const all: Emoji[] = [];
    const byShortcode = new Map<string, Emoji>();
    for (const entry of raw) {
      if (entry.group === undefined || entry.group === 2) continue;
      const found = codes[entry.hexcode];
      const shortcodes = found === undefined ? [] : Array.isArray(found) ? found : [found];
      const emoji: Emoji = {
        unicode: entry.unicode,
        label: entry.label,
        keywords: [entry.label.toLowerCase(), ...(entry.tags ?? []), ...shortcodes],
        shortcodes,
        group: entry.group,
        order: entry.order ?? 0,
      };
      all.push(emoji);
      for (const code of shortcodes) if (!byShortcode.has(code)) byShortcode.set(code, emoji);
    }
    all.sort((a, b) => a.order - b.order);
    const categories = GROUPS.map(({ group, key, label }) => ({
      key,
      label,
      emoji: all.filter((e) => e.group === group),
    }));
    loaded = { categories, all, byShortcode };
    return loaded;
  });
  return loading;
}

/**
 * Emoji matching a query, best first: an exact shortcode, then shortcodes and
 * names that start with it, then anything that merely contains it.
 */
export function searchEmoji(data: EmojiData, query: string, limit = 80): Emoji[] {
  const q = query.trim().toLowerCase().replace(/^:|:$/g, "");
  if (!q) return [];
  const scored: { emoji: Emoji; score: number }[] = [];
  for (const emoji of data.all) {
    let score = -1;
    if (emoji.shortcodes.includes(q)) score = 0;
    else if (emoji.shortcodes.some((c) => c.startsWith(q))) score = 1;
    else if (emoji.label.toLowerCase().startsWith(q)) score = 2;
    else if (emoji.keywords.some((k) => k.startsWith(q))) score = 3;
    else if (emoji.keywords.some((k) => k.includes(q))) score = 4;
    if (score >= 0) scored.push({ emoji, score });
  }
  scored.sort((a, b) => a.score - b.score || a.emoji.order - b.emoji.order);
  return scored.slice(0, limit).map((s) => s.emoji);
}

/* ─────────────────────────────── recently used ─────────────────────────────── */

/**
 * Kept in this browser, per person. A server column would follow somebody
 * between devices, but a recent row is a convenience, and an empty one on a
 * new laptop costs nothing.
 */
const RECENT_LIMIT = 16;

function recentKey(userId: string | undefined) {
  return `astrum:emoji-recent:${userId ?? "anon"}`;
}

export function readRecentEmoji(userId: string | undefined): string[] {
  try {
    const parsed: unknown = JSON.parse(window.localStorage.getItem(recentKey(userId)) ?? "[]");
    return Array.isArray(parsed) ? parsed.filter((v): v is string => typeof v === "string").slice(0, RECENT_LIMIT) : [];
  } catch {
    return [];
  }
}

export function rememberEmoji(userId: string | undefined, emoji: string): void {
  try {
    const next = [emoji, ...readRecentEmoji(userId).filter((e) => e !== emoji)].slice(0, RECENT_LIMIT);
    window.localStorage.setItem(recentKey(userId), JSON.stringify(next));
  } catch {
    // Private windows and full storage: the picker still works, without a row.
  }
}
